import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug } from '@/utils/utils';
import { AttachInternals, Component, Element, Event, EventEmitter, Host, Listen, Prop, State, Watch, h } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import { Configuration, ThemeConfig } from '@nylas/core';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE } from '@/common/constants';

/**
 * The `nylas-booking-confirmation-type` component is a UI component that allows the user to choose a custom redirect URL after a booking is confirmed.
 * @part nbct - The custom booking flow container
 * @part nbct__header - The header of the custom booking flow
 * @part nbct__dropdown - The dropdown container
 * @part nbct__dropdown-button - The dropdown button
 * @part nbct__dropdown-content - The dropdown content
 */
@Component({
  tag: 'nylas-booking-confirmation-type',
  styleUrl: 'nylas-booking-confirmation-type.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasBookingConfirmationType {
  @Element() host!: HTMLNylasBookingConfirmationTypeElement;
  /**
   * @standalone
   * The selected config
   */
  @Prop() selectedConfiguration?: Configuration;

  /**
   * @standalone
   * The name of the confirmation redirect link.
   */
  @Prop() name: string = 'confirmation-type';
  /**
   * @standalone
   * The booking type.
   */
  @Prop() bookingType: string = 'booking';
  /**
   * @standalone
   * Show or hide the confirmation type.
   * This prop is automatically determined based on the value of selectedConfiguration in the config,
   * making it unnecessary to set this prop manually.
   * If the configuration is a group configuration, the component will not be shown.
   */
  @Prop() showComponent: boolean = this.selectedConfiguration?.event_booking?.booking_type !== 'custom-confirmation' && this.selectedConfiguration?.type !== 'group';
  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;
  /**
   * @standalone
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;

  /**
   * The element internals.
   */
  @AttachInternals() internals!: ElementInternals;

  /**
   * The selected bookingType.
   */
  @State() selectedBookingType!: string;

  /**
   * The open/close state of the booking confirmation type dropdown.
   */
  @State() showDropdown!: boolean;
  /**
   * This event is fired when the selected bookingType changes.
   */
  @Event() valueChanged!: EventEmitter<{
    value: string;
    name: string;
    valueChanged?: (event: CustomEvent<{ value: string; name: string }>) => void;
  }>;

  /**
   * When a name prop is passed, stencil does not automatically set the name attribute on the host element.
   * Since this component is form-associated, the name attribute is required for form submission.
   * This is a workaround to ensure that the name attribute is set on the host element.
   */
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-booking-confirmation-type', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  @Watch('selectedConfiguration')
  selectedConfigurationChangedHandler(newValue: Configuration) {
    debug('nylas-booking-confirmation-type', 'selectedConfigurationChangedHandler', newValue);
    if (newValue?.event_booking?.booking_type) {
      this.selectedBookingType = newValue?.event_booking?.booking_type;
      // If the configuration is a group configuration, the component will not be shown.
      this.showDropdown = newValue?.event_booking?.booking_type !== 'custom-confirmation' && !!newValue?.scheduler?.organizer_confirmation_url && newValue?.type !== 'group';
      this.isInternalsAvailable && this.internals.setFormValue(this.selectedBookingType.toString(), this.name);
    }
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  // Lifecycle methods
  connectedCallback() {
    debug('nylas-booking-confirmation-type', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  disconnectedCallback() {
    debug('nylas-booking-confirmation-type', 'disconnectedCallback');
  }

  componentWillLoad() {
    debug('nylas-booking-confirmation-type', 'componentWillLoad');
    // See comment in the @Watch('name') decorator for more information.
    this.host.setAttribute('name', this.name);
  }

  componentDidLoad() {
    debug('nylas-booking-confirmation-type', 'componentDidLoad');
    if (this.selectedConfiguration) {
      this.selectedConfigurationChangedHandler(this.selectedConfiguration);
    } else {
      this.selectedBookingType = this.bookingType || 'booking';
      this.showDropdown = this.showComponent;
    }
    this.isInternalsAvailable && this.internals.setFormValue(this.selectedBookingType.toString(), this.name);
  }

  componentWillUpdate() {
    debug('nylas-booking-confirmation-type', 'componentWillUpdate');
  }

  componentDidUpdate() {
    debug('nylas-booking-confirmation-type', 'componentDidUpdate');
  }

  componentWillRender() {
    debug('nylas-booking-confirmation-type', 'componentWillRender');
  }

  componentDidRender() {
    debug('nylas-booking-confirmation-type', 'componentDidRender');
  }

  get isInternalsAvailable() {
    return typeof this.internals.setFormValue === 'function' && typeof this.internals.setFormValue === 'function';
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  @Listen('nylasFormDropdownChanged')
  nylasFormDropdownChangedHandler(event: CustomEvent<{ value: string; name: string }>) {
    debug('nylas-booking-confirmation-type', 'nylasFormDropdownChangedHandler', event.detail);
    // Pass as handler so that if event.defaultPrevented by parent app, this will be skipped.
    const valueChanged = (event: CustomEvent<{ value: string; name: string }>) => {
      const { value } = event.detail;
      this.selectedBookingType = value;
      this.internals.setFormValue(value.toString(), this.name);
    };
    this.valueChanged.emit({ ...event.detail, valueChanged });
  }

  @RegisterComponent<NylasBookingConfirmationType, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-booking-confirmation-type',
    stateToProps: new Map([
      ['schedulerConfig.selectedConfiguration', 'selectedConfiguration'],
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    eventToProps: {
      valueChanged: async (
        event: CustomEvent<{ value: string; name: string; valueChanged?: (event: CustomEvent<{ value: string; name: string }>) => void }>,
        _nylasSchedulerConfigConnector: NylasSchedulerConfigConnector,
      ) => {
        const { valueChanged } = event.detail;
        // If a handler is passed, call it.
        if (valueChanged) {
          valueChanged(event);
        }
      },
    },
    fireRegisterEvent: true,
  })
  render() {
    const bookingOptions = [
      {
        label: i18next.t('nylasBookingConfirmationType.bookingOption', { context: 'booking' }),
        value: 'booking',
      },
      {
        label: i18next.t('nylasBookingConfirmationType.bookingOption', { context: 'organizerConfirmation' }),
        value: 'organizer-confirmation',
      },
    ];

    return (
      <Host>
        {this.showDropdown && (
          <div class="nylas-booking-confirmation-type" part="nbct">
            {bookingOptions.length > 0 && (
              <div class="nylas-booking-confirmation-type__dropdown">
                <span class="label">
                  {i18next.t('nylasBookingConfirmationType.dropdownLabel')}
                  <tooltip-component>
                    <info-icon slot="tooltip-icon" />
                    <span slot="tooltip-content">{i18next.t('nylasBookingConfirmationType.tooltip.desc')}</span>
                  </tooltip-component>
                </span>
                {this.selectedBookingType && (
                  <select-dropdown
                    name="booking-type"
                    options={bookingOptions}
                    withSearch={false}
                    exportparts="sd_dropdown: nbct__dropdown, sd_dropdown-button: nbct__dropdown-button, sd_dropdown-content: nbct__dropdown-content"
                    defaultSelectedOption={bookingOptions.find(cal => cal.value == this.selectedBookingType)}
                    themeConfig={this.themeConfig}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </Host>
    );
  }
}
